import React, { useEffect } from 'react'
import { useFormikContext } from 'formik'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'

import SimpleInput from 'app/componentV4/Input/SimpleInput'
import LabelInput from 'app/componentV4/Labels/LabelInput'
import { makeStyles, useMediaQuery, IconButton, Icon } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { twMerge } from 'tailwind-merge'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import { CLOSE, SECONDARY, CANCEL } from 'app/constants/button'
import {
  openWarningDialog,
  closeWarningDialog,
} from 'app/store/actions/warningDialog.actions'
import { DATE_PICKER_FORMAT } from 'libs/dateUtils'
import SimpleDatePicker from 'app/componentV4/Input/SimpleDatePicker'
import SimpleAutocomplete from 'app/componentV4/selectors/SimpleAutocomplete'
import NationalitySelector from 'app/componentV4/selectors/NationalitySelector'
import CountrySelector from 'app/componentV4/selectors/CountrySelector'
import { getGender } from 'libs/gender'
import clsx from 'clsx'
import moment from 'moment'
import { isEmpty } from 'ramda'

const useStyles = makeStyles((theme) => ({
  formRoot: {
    '& .MuiFormControl-root': {
      '& .label-error': {
        marginTop: '4px',
      },
    },
  },
  inputFormat: {
    '& .MuiInputBase-root': {
      color: 'rgb(24 53 96 / var(--tw-text-opacity)) !important',
    },
    '& fieldset': {
      height: '53px !important',
      borderRadius: '.8rem !important',
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 17px) scale(1)',
    },
  },
  labelInput: {
    '& span': {
      color: '#EF4444',
    },
  },
}))

const GeneralInformationForm = ({
  subscriberId,
  countries,
  onGeneralChange,
  onGeneralReset,
  isShowTranslate,
  isShowDetect,
  checkTranslate,
  checkTranslateFirstState,
  onTranslate,
  closeWarningTranslate,
  closeDetact,
}) => {
  const formik = useFormikContext()

  const classes = useStyles()
  const { formatMessage } = useIntl()
  const isButtonDisabled = !formik.dirty || formik.isValidating
  const isButtonLoading = formik.isValidating || formik.isSubmitting
  const genders = getGender(formatMessage)
  const theme = useTheme()
  const isDownXs = useMediaQuery(theme.breakpoints.down('xs'))
  const dispatch = useDispatch()

  const onCancelClick = () => {
    if (formik.dirty) {
      const payload = {
        title: formatMessage({ id: 'dialog.discard.title' }),
        description: formatMessage({ id: 'dialog.discard.body' }),
        submitText: formatMessage({
          id: 'draft.notification.create.popup.title',
        }),
        closeText: formatMessage({ id: 'dialog.discard.close' }),
        type: CANCEL,
        onSubmit: () => {
          formik.resetForm()
          onGeneralReset()
          dispatch(closeWarningDialog())
        },
        onClose: () => {
          dispatch(closeWarningDialog())
        },
      }
      dispatch(openWarningDialog(payload))
    }
  }

  const handleTranslate = async () => {
    const { phoneticFirstName, phoneticLastName, phoneticMiddleName } =
      await onTranslate({
        subscriberId,
        firstName: formik.values.firstName,
        lastName: formik.values.lastName,
        middleName: formik.values.middleName,
      })
    closeWarningTranslate()
    formik.setFieldValue('phoneticFirstName', phoneticFirstName)
    formik.setFieldValue('phoneticLastName', phoneticLastName)
    formik.setFieldValue('phoneticMiddleName', phoneticMiddleName)
  }

  useEffect(() => {
    if (formik.dirty) {
      onGeneralChange(formik.dirty)
    } else {
      onGeneralReset()
    }
  }, [formik.dirty])

  useEffect(() => {
    const phoneticFullText = `${formik.values.phoneticFirstName}${formik.values.phoneticMiddleName}${formik.values.phoneticLastName}`
    if (isEmpty(phoneticFullText)) {
      const fullText = `${formik.values.firstName}${formik.values.middleName}${formik.values.lastName}`
      checkTranslateFirstState(fullText)
    }
  }, [])

  return (
    <div className={clsx('w-full', classes.formRoot)}>
      {isShowTranslate && (
        <div className="mb-5 bg-primary-lighter border border-primary-350 text-16 text-blue-950 rounded-8 py-5 items-center px-5">
          <div className="flex justify-between ">
            <div className="flex my-auto">
              <FormattedMessage
                id="subscriberDetail.automatically.translate"
                values={{
                  translateButton: (
                    <label
                      className="text-secondary px-2 cursor-pointer"
                      onClick={handleTranslate}
                    >
                      {formatMessage({
                        id: 'conversationRoom.translation.label',
                      })}
                    </label>
                  ),
                }}
              />
            </div>
            <div>
              <IconButton
                aria-owns="Close button"
                aria-haspopup="true"
                className="p-2"
                onClick={() => {
                  closeWarningTranslate()
                  closeDetact()
                }}
              >
                <Icon color="action" className="text-18 text-blue-950 font-600">
                  close
                </Icon>
              </IconButton>
            </div>
          </div>
        </div>
      )}
      {isShowDetect && (
        <div className="mb-5 bg-primary-lighter border border-primary-350 text-16 text-blue-950 rounded-8 py-5 items-center px-5">
          <div className="flex justify-between">
            <div className="flex my-auto">
              <FormattedMessage
                id="subscriberDetail.detect.translate"
                values={{
                  translateButton: (
                    <span
                      className="text-secondary px-2 cursor-pointer"
                      onClick={handleTranslate}
                    >
                      {formatMessage({
                        id: 'conversationRoom.translation.label',
                      })}
                    </span>
                  ),
                }}
              />
            </div>
            <div>
              <IconButton
                aria-owns="Close button"
                aria-haspopup="true"
                className="p-2"
                onClick={() => {
                  closeWarningTranslate()
                  closeDetact()
                }}
              >
                <Icon color="action" className="text-18 text-blue-950 font-600">
                  close
                </Icon>
              </IconButton>
            </div>
          </div>
        </div>
      )}
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput
          label={formatMessage({
            id: 'firstName.label',
          })}
          className="w-full capitalize"
          required
        >
          <SimpleInput
            id="firstName"
            name="firstName"
            type="text"
            value={formik.values.firstName}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(event) => {
              formik.setFieldValue('firstName', event.target.value)
              formik.setFieldTouched('firstName', true, false)
              const fullName = `${event.target.value}${formik.values.middleName}${formik.values.lastName}`
              checkTranslate(fullName)
            }}
            placeholder={formatMessage({
              id: 'firstName.label',
            })}
            error={formik.errors.firstName && formik.touched.firstName}
            helperText={formik.errors.firstName}
          />
        </LabelInput>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.phoneticFirstName',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="phoneticFirstName"
            name="phoneticFirstName"
            type="text"
            value={formik.values.phoneticFirstName}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('phoneticFirstName', e.target.value)
              formik.setFieldTouched('phoneticFirstName', true, false)
              if (
                isEmpty(e.target.value && !isEmpty(formik.values.firstName))
              ) {
                const fullText = `${formik.values.firstName}${formik.values.middleName}${formik.values.lastName}`
                checkTranslateFirstState(fullText)
              }
            }}
            placeholder={formatMessage({
              id: 'crm.form.phoneticFirstName',
            })}
            error={
              formik.errors.phoneticFirstName &&
              formik.touched.phoneticFirstName
            }
            helperText={formik.errors.phoneticFirstName}
          />
        </LabelInput>
      </div>
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.middleName',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="middleName"
            name="middleName"
            type="text"
            value={formik.values.middleName}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(event) => {
              formik.setFieldValue('middleName', event.target.value)
              formik.setFieldTouched('middleName', true, false)
              const fullName = `${formik.values.firstName}${event.target.value}${formik.values.lastName}`
              checkTranslate(fullName)
            }}
            placeholder={formatMessage({
              id: 'crm.form.middleName',
            })}
            error={formik.errors.middleName && formik.touched.middleName}
            helperText={formik.errors.middleName}
          />
        </LabelInput>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.phoneticMiddleName',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="phoneticMiddleName"
            name="phoneticMiddleName"
            type="text"
            value={formik.values.phoneticMiddleName}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('phoneticMiddleName', e.target.value)
              formik.setFieldTouched('phoneticMiddleName', true, false)
              if (
                isEmpty(e.target.value) &&
                !isEmpty(formik.values.middleName)
              ) {
                const fullText = `${formik.values.firstName}${formik.values.middleName}${formik.values.lastName}`
                checkTranslateFirstState(fullText)
              }
            }}
            placeholder={formatMessage({
              id: 'crm.form.phoneticMiddleName',
            })}
            error={
              formik.errors.phoneticMiddleName &&
              formik.touched.phoneticMiddleName
            }
            helperText={formik.errors.phoneticMiddleName}
          />
        </LabelInput>
      </div>
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput
          label={formatMessage({
            id: 'lastName.label',
          })}
          className="w-full capitalize"
          required
        >
          <SimpleInput
            id="lastName"
            name="lastName"
            type="text"
            value={formik.values.lastName}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(event) => {
              formik.setFieldValue('lastName', event.target.value)
              formik.setFieldTouched('lastName', true, false)
              const fullName = `${formik.values.firstName}${formik.values.middleName}${event.target.value}`
              checkTranslate(fullName)
            }}
            placeholder={formatMessage({
              id: 'lastName.label',
            })}
            error={formik.errors.lastName && formik.touched.lastName}
            helperText={formik.errors.lastName}
          />
        </LabelInput>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.phoneticLastName',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="phoneticLastName"
            name="phoneticLastName"
            type="text"
            value={formik.values.phoneticLastName}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('phoneticLastName', e.target.value)
              formik.setFieldTouched('phoneticLastName', true, false)
              if (isEmpty(e.target.value) && !isEmpty(formik.values.lastName)) {
                const fullText = `${formik.values.firstName}${formik.values.middleName}${formik.values.lastName}`
                checkTranslateFirstState(fullText)
              }
            }}
            placeholder={formatMessage({
              id: 'crm.form.phoneticLastName',
            })}
            error={
              formik.errors.phoneticLastName && formik.touched.phoneticLastName
            }
            helperText={formik.errors.phoneticLastName}
          />
        </LabelInput>
      </div>
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.prefix',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="prefix"
            name="prefix"
            type="text"
            value={formik.values.prefix}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('prefix', e.target.value)
              formik.setFieldTouched('prefix', true, false)
            }}
            error={formik.errors.prefix && formik.touched.prefix}
            helperText={formik.errors.prefix}
            placeholder={formatMessage({
              id: 'crm.form.prefix',
            })}
          />
        </LabelInput>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.suffix',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="suffix"
            name="suffix"
            type="text"
            value={formik.values.suffix}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('suffix', e.target.value)
              formik.setFieldTouched('suffix', true, false)
            }}
            placeholder={formatMessage({
              id: 'crm.form.suffix',
            })}
            error={formik.errors.suffix && formik.touched.suffix}
            helperText={formik.errors.suffix}
          />
        </LabelInput>
      </div>
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.formalTitle',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="formalTitle"
            name="formalTitle"
            type="text"
            value={formik.values.formalTitle}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('formalTitle', e.target.value)
              formik.setFieldTouched('formalTitle', true, false)
            }}
            placeholder={formatMessage({
              id: 'crm.form.formalTitle',
            })}
            error={formik.errors.formalTitle && formik.touched.formalTitle}
            helperText={formik.errors.formalTitle}
          />
        </LabelInput>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.nickname',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="nickname"
            name="nickname"
            type="text"
            value={formik.values.nickname}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('nickname', e.target.value)
              formik.setFieldTouched('nickname', true, false)
            }}
            placeholder={formatMessage({
              id: 'crm.form.nickname',
            })}
            error={formik.errors.nickname && formik.touched.nickname}
            helperText={formik.errors.nickname}
          />
        </LabelInput>
      </div>
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.dateOfBirth',
          })}
          className="w-full"
        >
          <SimpleDatePicker
            id="dateOfBirth"
            format={DATE_PICKER_FORMAT}
            placeholder="MM/DD/YYYY"
            value={formik.values.dateOfBirth}
            autoComplete="off"
            maxDate={moment()}
            className={twMerge('w-full', classes.inputFormat)}
            onChange={(date) => {
              formik.setFieldValue('dateOfBirth', date)
              formik.setFieldTouched('dateOfBirth', true, false)
            }}
            error={formik.errors.dateOfBirth && formik.touched.dateOfBirth}
            helperText={formik.errors.dateOfBirth}
          />
        </LabelInput>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.placeOfBirth',
          })}
          className="w-full"
        >
          <SimpleInput
            id="placeOfBirth"
            name="placeOfBirth"
            type="text"
            value={formik.values.placeOfBirth}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('placeOfBirth', e.target.value)
              formik.setFieldTouched('placeOfBirth', true, false)
            }}
            placeholder={formatMessage({
              id: 'crm.form.placeOfBirth',
            })}
            error={formik.errors.placeOfBirth && formik.touched.placeOfBirth}
            helperText={formik.errors.placeOfBirth}
          />
        </LabelInput>
      </div>
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.gender',
          })}
          className="w-full capitalize"
        >
          <SimpleAutocomplete
            id="gender"
            options={genders}
            placeholder={formatMessage({
              id: 'crm.form.gender',
            })}
            className={twMerge('w-full', classes.inputFormat)}
            value={formik.values.gender || null}
            onChange={(value) => {
              formik.setFieldValue('gender', value)
              formik.setFieldTouched('gender', true, false)
            }}
          />
        </LabelInput>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.nationality',
          })}
          className="w-full capitalize"
        >
          <NationalitySelector
            placeholder={formatMessage({
              id: 'crm.form.nationality',
            })}
            className={twMerge('w-full', classes.inputFormat)}
            value={formik.values.nationality || null}
            onChange={(value) => {
              formik.setFieldValue('nationality', value)
              formik.setFieldTouched('nationality', true, false)
            }}
          />
        </LabelInput>
      </div>
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput
          label={formatMessage({
            id: 'subscriber.label.uniqueId',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="uniqueId"
            name="uniqueId"
            type="text"
            value={formik.values.uniqueId}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('uniqueId', e.target.value)
              formik.setFieldTouched('uniqueId', true, false)
            }}
            placeholder={formatMessage({
              id: 'subscriber.label.uniqueId',
            })}
            error={formik.errors.uniqueId && formik.touched.uniqueId}
            helperText={formik.errors.uniqueId}
          />
        </LabelInput>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.passportId',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="passportId"
            name="passportId"
            type="text"
            value={formik.values.passportId}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('passportId', e.target.value)
              formik.setFieldTouched('passportId', true, false)
            }}
            placeholder={formatMessage({
              id: 'crm.form.passportId',
            })}
            error={formik.errors.passportId && formik.touched.passportId}
            helperText={formik.errors.passportId}
          />
        </LabelInput>
      </div>
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.passportCountryCode',
          })}
          className="w-full capitalize"
        >
          <CountrySelector
            placeholder={formatMessage({
              id: 'crm.form.passportCountryCode',
            })}
            countries={countries}
            className={twMerge('w-full', classes.inputFormat)}
            value={formik.values.passportCountryCode || null}
            onChange={(value) => {
              formik.setFieldValue(
                'passportCountryCode',
                isEmpty(value) ? '' : value.id,
              )
              formik.setFieldTouched('passportCountryCode', true, false)
            }}
          />
        </LabelInput>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.passportExpiresAt',
          })}
          className="w-full capitalize"
        >
          <SimpleDatePicker
            id="passportExpiresAt"
            format={DATE_PICKER_FORMAT}
            placeholder="MM/DD/YYYY"
            value={formik.values.passportExpiresAt}
            autoComplete="off"
            maxDate={moment().add(12, 'years')}
            className={twMerge('w-full', classes.inputFormat)}
            onChange={(date) => {
              formik.setFieldValue('passportExpiresAt', date)
              formik.setFieldTouched('passportExpiresAt', true, false)
            }}
            error={
              formik.errors.passportExpiresAt &&
              formik.touched.passportExpiresAt
            }
            helperText={formik.errors.passportExpiresAt}
          />
        </LabelInput>
      </div>
      <div className="flex justify-end space-x-5 mt-5">
        <ContainedButton
          color={CLOSE}
          onClick={onCancelClick}
          disabled={isButtonDisabled || isButtonLoading}
          type="button"
          className="w-96"
        >
          <div className="flex">
            <span className="normal-case">
              {formatMessage({
                id: 'action.cancel',
              })}
            </span>
          </div>
        </ContainedButton>
        <ContainedButton
          color={SECONDARY}
          isLoading={isButtonLoading}
          disabled={isButtonDisabled || isButtonLoading}
          type="submit"
          onClick={() => closeDetact()}
        >
          <div className="flex">
            <span className="normal-case">
              {formatMessage({
                id: 'subscriberCrm.editGroup.save.label',
              })}
            </span>
          </div>
        </ContainedButton>
      </div>
    </div>
  )
}

export default GeneralInformationForm
